type ColorInput = string; // hex or rgb

/**
 * Converts a color (hex or rgb) to RGB values
 */
function parseColor(color: ColorInput): { r: number; g: number; b: number } {
  const trimmed = color?.trim() ?? "";
  const defaultColor = { r: 0, g: 0, b: 0 };

  // Handle hex colors
  if (trimmed?.startsWith("#")) {
    const hex = trimmed.slice(1);

    // Handle 3-digit hex (e.g., #f00)
    if (hex.length === 3) {
      const [r, g, b] = hex.split("")?.map((char) => parseInt(char + char, 16));
      return { r, g, b };
    }

    // Handle 6-digit hex (e.g., #ff0000)
    if (hex.length === 6) {
      const [r, g, b] = hex.match(/.{2}/g)!.map((pair) => parseInt(pair, 16));
      return { r, g, b };
    }

    return defaultColor;
  }

  // Handle rgb colors
  if (trimmed?.startsWith("rgb(")) {
    const numbers = trimmed
      .slice(4, -1) // Remove "rgb(" and ")"
      .split(",") // Split by comma
      .map((n) => parseInt(n?.trim())); // Parse each number

    return { r: numbers?.[0], g: numbers?.[1], b: numbers?.[2] };
  }
  return defaultColor;
}

/**
 * Calculates the relative luminance of a color
 */
function getLuminance(r: number, g: number, b: number): number {
  // Convert RGB to relative luminance
  const toLinear = (c: number): number => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };

  const rLinear = toLinear(r);
  const gLinear = toLinear(g);
  const bLinear = toLinear(b);

  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

/**
 * Determines if a color is dark or light and returns appropriate contrast color
 * @param primaryColor - Color in hex (#rrggbb or #rgb) or rgb(r, g, b) format
 * @param lightColor - Color to return for light backgrounds (default: "#000000")
 * @param darkColor - Color to return for dark backgrounds (default: "#ffffff")
 * @returns "#ffffff" for dark colors, "#000000" for light colors
 */
export function getContrastColor(
  primaryColor: ColorInput,
  lightColor: string = "#000000",
  darkColor: string = "#ffffff",
): string {
  const { r, g, b } = parseColor(primaryColor);
  const luminance = getLuminance(r, g, b);

  // WCAG threshold is 0.179 for determining if white or black text should be used
  // Colors with luminance > 0.179 are considered light, others are dark
  return luminance > 0.179 ? lightColor : darkColor;
}
